import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
	ChevronLeftIcon,
	ExclamationTriangleIcon,
	LockClosedIcon,
} from '@heroicons/react/20/solid'

import LoadCard from '../Loadboard/Load'
import useBoardLayout from '../Loadboard/useBoardLayout'
import PaymentModal from '../Loadboard/PaymentModal'
import PostATruckModal from '../Loadboard/PostATruckModal/PostATruckModal'
import Sidebar from '../Sidebar'
import UserDropdown from '../UserDropdown'
import AppLauncher from '../AppLauncher'
import Navbar from '../LandingPage/Navbar'
import { Footer } from '../LandingPage/Footer'
import { DarkModeContext, useDarkMode } from '../../context/DarkModeContext'
import { useSidebar } from '../../context/SidebarContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { getSharedLoad } from '../../services/httpService'
import auth from '../../services/authService'

// Mirrors the loadboard's own width preference so a shared load is laid out
// exactly like a row on the board.
const boardMaxWidthClass = () => {
	try {
		return localStorage.getItem('loadfetcher-content-width') === 'wide'
			? 'max-w-[1640px]'
			: 'max-w-screen-xl'
	} catch {
		return 'max-w-screen-xl'
	}
}

const LIGHT_THEME = { isDarkMode: false, toggleDarkMode: () => {} }

const cityLine = address => {
	if (!address?.city) return '—'
	return `${address.city}, ${address.state}`
}

function SharedLoad() {
	const { token } = useParams()
	const { isDarkMode: appDarkMode } = useDarkMode()
	const { sidebarWidth } = useSidebar()
	const { subscriptionData } = useSubscription()
	const { isMobile, isTablet } = useBoardLayout()

	const [share, setShare] = useState(null)
	const [status, setStatus] = useState('loading')
	const [showUpgrade, setShowUpgrade] = useState(false)
	const [showPostTruck, setShowPostTruck] = useState(false)

	const user = auth.getCurrentUser()

	
	const isPublicView = !user
	const isDarkMode = isPublicView ? false : appDarkMode

	
	useEffect(() => {
		if (!isPublicView) return

		const style = document.createElement('style')
		style.textContent = 'html { background-color: #ffffff !important; }'
		document.head.appendChild(style)

		return () => style.remove()
	}, [isPublicView])

	useEffect(() => {
		let cancelled = false

		const load = async () => {
			setStatus('loading')

			try {
				const data = await getSharedLoad(token)
				if (cancelled) return

				setShare(data)
				setStatus('ready')
			} catch (error) {
				if (cancelled) return

				setStatus(error?.response?.status === 404 ? 'missing' : 'error')
			}
		}

		load()
		return () => {
			cancelled = true
		}
	}, [token])

	const load = share?.load
	const hasFullAccess = share?.access === 'full'

	// Same test the loadboard applies: paid, or still inside the trial.
	const isSubscribed = Boolean(
		subscriptionData?.payed ||
			(subscriptionData?.trialEndTime &&
				new Date(subscriptionData.trialEndTime) > new Date())
	)

	useEffect(() => {
		if (!load) return

		document.title = `${cityLine(load.pickup?.address)} → ${cityLine(
			load.dropOff?.address
		)} · LoadFetcher`
	}, [load])

	const card = isDarkMode
		? 'rounded-xl border border-gray-700 bg-[#22272e]'
		: 'rounded-xl border border-gray-200 bg-white shadow-sm'

	const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500'

	const signupHref = `/account/signup?next=${encodeURIComponent(
		`/l/${token}`
	)}`
	const loginHref = `/account/login?next=${encodeURIComponent(`/l/${token}`)}`

	const renderLockedPanel = () => (
		<div className={`${card} p-8 text-center`}>
			<LockClosedIcon className={`mx-auto h-6 w-6 ${muted}`} aria-hidden="true" />
			<p className="mx-auto mt-3 max-w-sm text-sm font-medium">
				Log in to see the rate, load details and stop details on this load.
			</p>

			<div className="mt-5 flex flex-wrap items-center justify-center gap-2">
				<Link
					to={loginHref}
					className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
				>
					Log in
				</Link>
				<Link
					to={signupHref}
					className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
						isDarkMode
							? 'border-gray-600 text-gray-200 hover:bg-gray-700'
							: 'border-gray-300 text-gray-700 hover:bg-gray-50'
					}`}
				>
					Sign up free
				</Link>
			</div>
		</div>
	)

	const renderMessage = (title, body) => (
		<div className={`${card} p-8 text-center`}>
			<ExclamationTriangleIcon
				className={`mx-auto h-8 w-8 ${muted}`}
				aria-hidden="true"
			/>
			<h1 className="mt-3 text-lg font-semibold">{title}</h1>
			<p className={`mt-2 text-sm ${muted}`}>{body}</p>
			<Link
				to="/"
				className="mt-5 inline-block rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
			>
				Browse loads on LoadFetcher
			</Link>
		</div>
	)

	
	const renderBackLink = () => (
		<Link
			to="/loadboard"
			className={`mb-3 inline-flex items-center gap-1 text-sm font-medium transition-colors ${
				isDarkMode
					? 'text-gray-400 hover:text-gray-200'
					: 'text-gray-500 hover:text-gray-700'
			}`}
		>
			<ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
			All loads
		</Link>
	)

	const renderSkeleton = () => (
		<div className={`${card} animate-pulse p-5 sm:p-6`}>
			<div
				className={`h-7 w-2/3 rounded ${
					isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
				}`}
			/>
			<div
				className={`mt-4 h-4 w-1/3 rounded ${
					isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
				}`}
			/>
			<div
				className={`mt-8 h-20 w-full rounded ${
					isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
				}`}
			/>
		</div>
	)

	const renderBody = () => {
		if (status === 'loading') return renderSkeleton()

		if (status === 'missing')
			return renderMessage(
				'This load link is no longer available',
				'Shared links expire once the load leaves the board. Ask the sender for a fresh link, or search live loads yourself.'
			)

		if (status === 'error' || !load)
			return renderMessage(
				'We could not open this load',
				'Something went wrong on our end. Try again in a moment.'
			)

		// Both viewers get the same card the loadboard renders, opened. A signed-out
		// one gets the login gate where the details would be.
		return (
			<LoadCard
				load={load}
				defaultExpanded
				hideShare
				isMobile={isMobile}
				isTablet={isTablet}
				locked={!hasFullAccess}
				lockedContent={hasFullAccess ? null : renderLockedPanel()}
				isSubscribed={isSubscribed}
				onUpgradeRequired={() => setShowUpgrade(true)}
				onPostTruck={() => setShowPostTruck(true)}
			/>
		)
	}

	const renderModals = () => (
		<>
			<PaymentModal
				isOpen={showUpgrade}
				onClose={() => setShowUpgrade(false)}
				isDarkMode={isDarkMode}
				initialBillingCycle="yearly"
			/>

			<PostATruckModal
				isOpen={showPostTruck}
				onClose={() => setShowPostTruck(false)}
				load={load}
				isDarkMode={isDarkMode}
			/>
		</>
	)

	// Signed-in viewers get the app chrome, so a shared link lands them somewhere
	// that feels like the product rather than a detached public page.
	if (user) {
		return (
			<div style={{ backgroundColor: isDarkMode ? '#1c2127' : '#ffffff' }}>
				<Sidebar />

				<div
					style={{
						paddingLeft: `${sidebarWidth}px`,
						transition: 'padding-left 300ms ease-in-out',
					}}
				>
					<div
						className="top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 px-4 sm:px-6 lg:px-8"
						style={{ backgroundColor: isDarkMode ? '#1c2127' : '#ffffff' }}
					>
						<div className="ml-auto flex items-center gap-x-3">
							<AppLauncher />
							<UserDropdown />
						</div>
					</div>

					<main
						className={`min-h-screen overflow-hidden rounded-tl-lg ${
							isDarkMode ? 'bg-dark-color' : 'bg-white'
						}`}
					>
						<div className={`mx-auto w-full ${boardMaxWidthClass()}`}>
							<div className="mb-5 px-4 py-2 sm:px-6 lg:px-8">
								{renderBackLink()}
								{renderBody()}
							</div>
						</div>
					</main>
				</div>

				{renderModals()}
			</div>
		)
	}

	return (
		<DarkModeContext.Provider value={LIGHT_THEME}>
			<div data-theme="light" className="flex min-h-screen flex-col bg-white pt-28">
				<Navbar minimal signInTo={loginHref} />

				<main className={`mx-auto w-full flex-1 ${boardMaxWidthClass()}`}>
					<div className="mb-5 px-4 py-6 sm:px-6 lg:px-8">{renderBody()}</div>
				</main>

				<Footer />

				{renderModals()}
			</div>
		</DarkModeContext.Provider>
	)
}

export default SharedLoad
